import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const BDIList = ({ bdiAssessments, isLoading, error, onAddBDI, onEditBDI, onDeleteBDI }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const records = bdiAssessments || [];

    const filteredRecords = records.filter((r) =>
        Object.values(r).some(
            (v) => v != null && String(v).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This BDI-II assessment will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                onDeleteBDI(id); // dispatch thunk
                Swal.fire("Deleted!", "BDI-II assessment deleted successfully.", "success");
            }
        });
    };

    // Show loading or error if needed
    if (isLoading) {
        return (
            <div className="text-center p-5">
                <i className="ri-loader-4-line ri-spin fs-2"></i>
                <p>Loading BDI-II assessments...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-danger p-5">
                <i className="ri-error-warning-line fs-2"></i>
                <p>{error}</p>
            </div>
        );
    }

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Beck Depression Inventory-II (BDI-II) Reports", 14, 10);
        const tableColumn = ["#", "Client Name", "Assessment Date", "Total Score", "Depression Level", "Assessed By"];
        const tableRows = filteredRecords.map((r, index) => [
            index + 1,
            r.client_name || "",
            r.assessment_date || "",
            r.total_score || 0,
            r.depression_level || "",
            r.assessed_by || ""
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: "grid",
            styles: { fontSize: 10 },
            headStyles: { fillColor: [59, 130, 246] }
        });
        doc.save("bdi_ii_assessments.pdf");
    };

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20].map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        { name: "S.No", cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "80px" },
        { name: "Client Name", selector: (row) => row.client_name || "", sortable: true },
        { name: "Assessment Date", selector: (row) => row.assessment_date || "", sortable: true },
        { name: "Total Score", selector: (row) => row.total_score || 0, sortable: true, center: true },
        {
            name: "Depression Level",
            cell: (row) => {
                let badge = "bg-success-subtle text-success";
                if (row.total_score >= 14 && row.total_score <= 19) badge = "bg-info-subtle text-info";
                if (row.total_score >= 20 && row.total_score <= 28) badge = "bg-warning-subtle text-warning";
                if (row.total_score >= 29) badge = "bg-danger-subtle text-danger";

                return <span className={`badge ${badge} rounded-pill px-3 py-2 fw-bold`}>{row.depression_level || "—"}</span>;
            },
            sortable: true,
            center: true,
        },
        { name: "Assessed By", selector: (row) => row.assessed_by || "", sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditBDI && (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => onEditBDI(row.id)}>
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteBDI && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "120px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
                            <li>
                                <CSVLink data={filteredRecords} filename="bdi_ii_assessments.csv" className="dropdown-item">
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search BDI-II assessments..."
                        className="form-control form-control-sm"
                        style={{ width: "280px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    {onAddBDI && (
                        <button className="btn btn-sm btn-primary" onClick={onAddBDI}>
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filteredRecords}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={setRowsPerPage}
                onChangePage={setCurrentPage}
                paginationComponent={CustomPagination}
                highlightOnHover
                striped
                responsive
                noDataComponent={
                    <div className="p-5 text-center text-muted">
                        <i className="ri-file-list-3-line fs-1 d-block mb-2"></i>
                        No BDI-II assessments found
                    </div>
                }
            />
        </div>
    );
};

export default BDIList;